import { defineComponent as _defineComponent } from 'vue'
import {
	ModuleContentTitleTrend,
	PopularityRankTypes,
	StreamingChartObjectType,
	StreamingChartsFilter,
	StreamingChartsTitlesEdge,
} from '@/@types/graphql-types';
import WatchNowButtonNew from '@/components/buttons/WatchNowButtonNew.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import ProvideStreamingChartData from '@/components/streaming-charts/ProvideStreamingChartData.vue';
import SingleRankingDisplay from '@/components/streaming-charts/SingleRankingDisplay.vue';
import StreamingChartsList from '@/components/streaming-charts/StreamingChartsList.vue';
import FilterSelect from '@/components/streaming-charts/ui/FilterSelect.vue';
import StreamingChartContactForm from '@/pages/landing-pages/streaming-charts/StreamingChartContactForm.vue';
import GuideItemCondensed from '@/pages/landing-pages/streaming-charts/GuideItemCondensed.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { WebLocale } from '@/enums/web-locale';
import UseArticleList from '@/helpers/composables/useArticleList';
import { useLanguageStore, useRoutingStore } from '@/helpers/composables/useStores';
import { Locale } from '@/helpers/locale-helper';
import { addToast } from '@/helpers/toast/toast-helper';
import { getVm } from '@/helpers/vm-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import BasicButton from '@/ui-components/BasicButton.vue';
import { computed, onMounted, ref } from 'vue';
import { ShareMethod } from '@/enums/share-methods';

import { faTwitter, faFacebook } from '@fortawesome/free-brands-svg-icons';
import { faCopy as faCopyRegular } from '@fortawesome/free-regular-svg-icons';
import { WEB_LOCALES_EMOJIS } from '@/constants/web-locales.constant';
import {
	reverseCategoryQueryMapping,
	reverseObjectTypeQueryMapping,
} from '@/components/details/streaming-chart/streaming-chart-config';

export default /*#__PURE__*/_defineComponent({
  __name: 'StreamingCharts',
  setup(__props) {

const { language, locales, webLocale } = useLanguageStore();
const { urlMetadata } = useRoutingStore();
const { t, tc } = useI18n();
const vm = getVm();

const farCopyIcon = faCopyRegular;

const filterTitleObjectType = ref<StreamingChartObjectType>(
	(reverseObjectTypeQueryMapping[
		vm.$route.query.t as keyof typeof reverseObjectTypeQueryMapping
	] as StreamingChartObjectType) ?? StreamingChartObjectType.Movie
);

const filterCountry = ref<WebLocale | null>();
const filterCategory = ref<PopularityRankTypes>(
	(reverseCategoryQueryMapping[
		vm.$route.query.ct as keyof typeof reverseCategoryQueryMapping
	] as PopularityRankTypes) ?? PopularityRankTypes.WeeklyPopularitySameContentType
);

const streamingChartsFilters = computed<StreamingChartsFilter>(() => {
	return {
		objectType: filterTitleObjectType.value,
		category: filterCategory.value,
	};
});

const logoSrc = {
	png: `/${ASSETS_DIR}/img/logo/JustWatch-logo-small.png`,
	webp: `/${ASSETS_DIR}/img/logo/JustWatch-logo-small.webp`,
};

const selectFiltersObjectTypes = computed(() => [
	{ name: tc('WEBAPP_MOVIES'), value: StreamingChartObjectType.Movie },
	{ name: tc('WEBAPP_TVSHOWS'), value: StreamingChartObjectType.Show },
	{ name: tc('WEBAPP_SEASONS'), value: StreamingChartObjectType.ShowSeason },
]);
const selectFiltersCountries = computed(() => {
	const countries = ((locales.value as Locale[] | null) || []).map(item => {
		const localizedCountryName = item.country_names[language.value] ?? item.country;
		return {
			name: localizedCountryName,
			value: item.webLocale.toUpperCase() || (null as WebLocale | null),
			webLocale: item.webLocale,
		};
	});

	countries.sort((a, b) => {
		const nameA = a.name.toUpperCase();
		const nameB = b.name.toUpperCase();
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}
		return 0;
	});

	const filteredCountries = countries.filter(country => {
		// prettier-ignore
		return ['US', 'UK', 'DE', 'FR', 'ES', 'IT', 'IN', 'MX', 'AU', 'BR', 'CA', 'TR', 'AR', 'EG', 'PL', null].includes(country.value);
	});

	filteredCountries.map(country => {
		if (country.value === 'UK') country.value = 'GB';

		country.name = `${country.webLocale ? WEB_LOCALES_EMOJIS[country.webLocale] : ''} ${country.name}`;
	});

	filteredCountries.unshift({ name: 'Global', value: null, webLocale: WebLocale.us });

	return filteredCountries;
});
const selectFiltersCategories = computed(() => [
	{ name: tc('WEBAPP_DAILY'), value: PopularityRankTypes.DailyPopularitySameContentType },
	{ name: tc('WEBAPP_WEEKLY'), value: PopularityRankTypes.WeeklyPopularitySameContentType },
	{ name: tc('WEBAPP_MONTHLY'), value: PopularityRankTypes.MonthlyPopularitySameContentType },
]);

const streamingChartFilters = [selectFiltersObjectTypes, selectFiltersCountries, selectFiltersCategories];

const getTitleName = (title: StreamingChartsTitlesEdge) => {
	if (streamingChartsFilters.value.objectType === StreamingChartObjectType.ShowSeason) {
		// @ts-ignore
		return `${title?.node?.show?.content?.title} (${title?.node?.content?.title})`;
	}
	return title?.node?.content?.title;
};

const getHeroSpinningText = (title: StreamingChartsTitlesEdge) => {
	// @ts-ignore
	const titleName = title?.node?.show
		? // @ts-ignore
		  `"${title.node.show.content?.title}" (${title.node.content.title})`
		: `"${title.node.content.title}"`;

	if (title.streamingChartInfo?.trend === ModuleContentTitleTrend.New)
		return t('WEBAPP_STREAMING_CHARTS_DEBUT', { title: titleName });

	if (title.streamingChartInfo?.trend === ModuleContentTitleTrend.Stable)
		return t('WEBAPP_STREAMING_CHARTS_HOLD', { title: titleName });

	if (
		title.streamingChartInfo?.trend === ModuleContentTitleTrend.Up &&
		title.streamingChartInfo?.topRank === 1 &&
		title.streamingChartInfo.daysInTop3 > 1
	)
		return t('WEBAPP_STREAMING_CHARTS_REGAIN', { title: titleName });

	return `${t('WEBAPP_STREAMING_CHARTS_SPIN', { title: titleName })} ${
		title.streamingChartInfo?.trendDifference && title.streamingChartInfo?.trendDifference > 0
			? t('WEBAPP_STREAMING_CHARTS_SPIN_RANKCHANGE', {
					rankChange: `+${formatTrendDifference(title.streamingChartInfo?.trendDifference)}`,
			  })
			: ''
	}`;
};

const formatTrendDifference = (trendDifference: number) => {
	return Intl.NumberFormat(language.value).format(trendDifference);
};

const getFirstPosterProps = (edge: StreamingChartsTitlesEdge): PosterGraphqlProps => {
	const title = edge.node;
	return {
		titleId: title.id,
		titleObjectId: title.objectId,
		titleObjectType: title.objectType,
		titleName: title.content.title,
		titlePosterUrl: title.content.posterUrl ?? '',
		titleBackdrops: title.content.backdrops,
		titleScoring: title.content.scoring,
		titleAvailableTo: title.availableTo,
		// titleUpcomingReleases: title.content.upcomingReleases,
		// seenEpisodeCount: (title.seenState || {}).seenEpisodeCount,
		isInWatchlist: false,
		isInSeenList: false,
		isInLikeList: false,
		isInDislikeList: false,
		imageProfile: 'poster_detail',
		lazy: true,
		showAvailabilityBadge: false,
	};
};

const onFilterChange = async (filterName: 'objectType' | 'country' | 'category', value: string | null) => {
	switch (filterName) {
		case 'objectType': {
			filterTitleObjectType.value = value as StreamingChartObjectType;

			if (value === StreamingChartObjectType.Movie) {
				vm.$router.replace({
					query: {
						...vm.$route.query,
						t: undefined,
					},
				});
				return;
			}

			const queryMapping = {
				MOVIE: 'movies',
				SHOW: 'shows',
				SHOW_SEASON: 'seasons',
			};

			vm.$router.replace({
				query: {
					...vm.$route.query,
					t: queryMapping[value as StreamingChartObjectType],
				},
			});

			break;
		}
		case 'country': {
			filterCountry.value = value as WebLocale | null;

			if (filterCountry.value === null) {
				vm.$router.replace({
					query: {
						...vm.$route.query,
						c: 'global',
					},
				});
				return;
			}

			vm.$router.replace({
				query: {
					...vm.$route.query,
					c: undefined,
				},
			});

			if (filterCountry.value.toLowerCase() === (webLocale.value === 'uk' ? 'gb' : webLocale.value)) return;

			const streamingChartLocales = urlMetadata.value.href_lang_tags!.map((lt: { href: string | any[] }) => ({
				country: lt.href.slice(1, 3),
				href: lt.href,
			}));

			const webLocaleValue = value === 'GB' ? 'UK' : value;

			const localisedStreamingChartsPage = streamingChartLocales.find(
				(tl: { country: string | undefined }) => tl.country === webLocaleValue?.toLowerCase()
			);

			if (localisedStreamingChartsPage) {
				await useLanguageStore().setWebLocale(webLocaleValue?.toLowerCase());
				await vm.$router.push({ path: localisedStreamingChartsPage.href, query: { ...vm.$route.query } });
				await vm.$nextTick();
			}
			break;
		}
		case 'category': {
			filterCategory.value = value as PopularityRankTypes;

			if (value === PopularityRankTypes.WeeklyPopularitySameContentType) {
				vm.$router.replace({
					query: {
						...vm.$route.query,
						ct: undefined,
					},
				});
				return;
			}

			const queryMapping = {
				DAILY_POPULARITY_SAME_CONTENT_TYPE: 'daily',
				WEEKLY_POPULARITY_SAME_CONTENT_TYPE: 'weekly',
				MONTHLY_POPULARITY_SAME_CONTENT_TYPE: 'monthly',
			};

			vm.$router.replace({
				query: {
					...vm.$route.query,
					ct: queryMapping[value as PopularityRankTypes],
				},
			});
			break;
		}
	}
};

onMounted(() => {
	filterTitleObjectType.value = reverseObjectTypeQueryMapping[
		vm.$route.query.t as keyof typeof reverseObjectTypeQueryMapping
	] as StreamingChartObjectType;

	filterCountry.value =
		vm.$route.query.c === 'global'
			? null
			: webLocale.value.toUpperCase() === 'UK'
			? 'GB'
			: webLocale.value.toUpperCase();

	filterCategory.value =
		(reverseCategoryQueryMapping[
			vm.$route.query.ct as keyof typeof reverseCategoryQueryMapping
		] as PopularityRankTypes) ?? PopularityRankTypes.WeeklyPopularitySameContentType;
});

const share = (title: StreamingChartsTitlesEdge, method?: ShareMethod) => {
	const shareUrl = window.location.href;
	const shareText = getHeroSpinningText(title);

	switch (method) {
		case ShareMethod.FACEBOOK:
			{
				const url: string = new URL(
					`?u=${shareUrl}&t=${shareText}`,
					'http://www.facebook.com/sharer/sharer.php'
				).toString();
				window.open(url);
			}
			break;
		case ShareMethod.TWITTER:
			{
				const url: string = new URL(
					`?url=${shareUrl}&text=${shareText}`,
					'https://twitter.com/intent/tweet'
				).toString();
				window.open(url);
			}
			break;
		default:
			if (navigator && navigator.clipboard) {
				navigator.clipboard.writeText(shareUrl);
				addToast({ message: getVm().$t('WEBAPP_COPIED_TO_CLIPBOARD') });
			}
	}
};

return { __sfc: true,language, locales, webLocale, urlMetadata, t, tc, vm, farCopyIcon, filterTitleObjectType, filterCountry, filterCategory, streamingChartsFilters, logoSrc, selectFiltersObjectTypes, selectFiltersCountries, selectFiltersCategories, streamingChartFilters, getTitleName, getHeroSpinningText, formatTrendDifference, getFirstPosterProps, onFilterChange, share, WatchNowButtonNew, PosterGraphql, ProvideStreamingChartData, SingleRankingDisplay, StreamingChartsList, FilterSelect, StreamingChartContactForm, GuideItemCondensed, HiddenHorizontalScrollbar, UseArticleList, BasicButton, faTwitter, faFacebook }
}

})